import Webcam from 'react-webcam';
import { Alert, Button } from 'antd';
import { useCallback, useRef, useState } from 'react';
import { Flex } from '..';
import { convertBase64ToFile } from '@/utils/func';
import { FacingMode } from '@/hooks/useFacingMode';
import { IoCameraReverseOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';

export interface UploadFaceCameraProps {
  facingMode: FacingMode;
  toggleFacingMode: () => void;
  setFile: (newFile: File) => void;
}

export default function UploadFaceCamera({
  facingMode,
  toggleFacingMode,
  setFile
}: UploadFaceCameraProps) {

  const [t] = useTranslation(['common', 'face']);
  const isSupported = 'mediaDevices' in window.navigator;
  const webcamRef = useRef<Webcam>(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState<React.ReactElement | string>(!isSupported ? t('face:errors.capture-support') : undefined);

  const handleCapture = useCallback(async () => {
    if (!webcamRef.current) return;
    setImageSrc(null);
    const imageSrc = webcamRef.current.getScreenshot();
    setImageSrc(imageSrc);
    const file = await convertBase64ToFile(imageSrc, 'image.png', 'image/png');
    setFile(file);
  }, [webcamRef, setImageSrc, setFile]);

  if (error) {
    return (
      <Flex direction="column" className="mx-auto" justify="center" align="center">
        <Alert type="error" message={error} />
      </Flex>
    );
  }

  return (
    <Flex className="w-full h-full relative" align="center" justify="center" direction="column">
      {imageSrc ?
        (
          <>
            <img className="max-w-[100%] max-h-[100%] h-full object-cover" src={imageSrc} />
            <Button
              danger
              size="middle"
              type="dashed"
              onClick={() => {
                setImageSrc(null);
                setFile(null);
              }}
              className="absolute bottom-3">
              {t('face:labels.remove-photo')}
            </Button>
          </>
        ) :
        <>
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/png"
            onUserMediaError={(e: any) => {
              if ((e.message as string)?.toLowerCase().includes('permission')) {
                return setError(t('face:labels.take-photo'));
              }
              setError(<>{t('face:errors.show-camera')}: <br />{e.message}</>);
            }}
            videoConstraints={{
              facingMode,
              aspectRatio: 1 / 1,
            }}
            screenshotQuality={1}
            forceScreenshotSourceSize={true}
            className=" h-full object-cover max-w-[100%] max-h-[300px]"
          />
          <Flex className="bg-[#efefef] rounded-full cursor-pointer absolute top-4 right-4 p-1.5">
            <IoCameraReverseOutline
              onClick={toggleFacingMode}
              className=" text-3xl select-none" />
          </Flex>
          <Button
            size="middle"
            type="dashed"
            onClick={handleCapture}
            className="absolute bottom-3">
            {t('face:labels.take-photo')}
          </Button>
        </>
      }
    </Flex>
  );
}
